import api from './api';

const entityMktSettingsService = {
  // Generic marketplace credential test (auth + system-specific verification).
  testCredentials: (marketplaceId, settings) =>
    api.post('/partnermktsettings/test-credentials', { marketplaceId, settings }),

  // Same test, for an already-SAVED credential. Used where a screen references a credential but does not
  // hold its values — the lambda reads and decrypts them, so the secret never reaches the browser.
  // `overrides` ([{name,value}]) are applied on top of the stored settings for the test only — a
  // chain/partner credential has no HOTELID of its own, and testing it without the per-hotel value
  // answers a different question from "does this work for this hotel".
  testSavedCredential: (credentialId, overrides) =>
    api.post('/partnermktsettings/test-credentials', { credentialId, ...(overrides ? { overrides } : {}) }),

  // ONE credential by id, in the shape the shared credential editor (CredentialsTab's CredentialModal) takes, plus the
  // entity that owns it (entity_type / entity_id). Audited as a READ. Used by CredentialModalById.
  getCredential: async (credentialId) => {
    const response = await api.get(`/partnermktsettings/credential?credentialId=${encodeURIComponent(credentialId)}`);
    return response.data.credential;
  },

  // WHERE ONE CREDENTIAL IS USED, by product: { products: [{ key, label, items: [{ id, name, role, status, hotel, chain,
  // updated, link }], error? }], total }. The credential editor's Usage tab.
  getUsage: async (credentialId) => {
    const response = await api.get(`/partnermktsettings/usage?credentialId=${encodeURIComponent(credentialId)}`);
    return response.data;
  },

  listCredentials: async (entityType, entityId) => {
    const response = await api.get(`/partnermktsettings?entityType=${entityType}&entityId=${entityId}`);
    return response.data;
  },

  saveCredentials: async (entityType, entityId, marketplaceId, name, settings, credentialId) => {
    const response = await api.post('/partnermktsettings', { entityType, entityId, marketplaceId, name, settings, credentialId });
    return response.data;
  },

  deleteCredentials: async (marketplaceId, credentialId) => {
    const params = new URLSearchParams({ credentialId });
    const response = await api.delete(`/partnermktsettings/${marketplaceId}?${params.toString()}`);
    return response.data;
  },

  getShares: async (entityType, entityId, marketplaceId, credentialId) => {
    const params = new URLSearchParams({ entityType, entityId, marketplaceId, credentialId });
    const response = await api.get(`/partnermktsettings/shares?${params.toString()}`);
    return response.data;
  },

  saveShares: async (entityType, entityId, marketplaceId, credentialId, shares) => {
    const response = await api.post('/partnermktsettings/shares', { entityType, entityId, marketplaceId, credentialId, shares });
    return response.data;
  },

  getSharedWithEntity: async (entityType, entityId) => {
    const response = await api.get(`/partnermktsettings/shared-with?entityType=${entityType}&entityId=${entityId}`);
    return response.data;
  },

  getTaskSharedCredentials: async (syncId) => {
    const response = await api.get(`/partnermktsettings/task-credentials?syncId=${syncId}`);
    return response.data;
  },

  saveSharedOverrides: async (entityType, entityId, marketplaceId, overrides) => {
    const response = await api.post('/partnermktsettings/shared-with', { entityType, entityId, marketplaceId, overrides });
    return response.data;
  },

  // Picker — single source of truth used by sync/PP/vault credential pickers
  // uapiEnabled: 'Y' asks for credentials across EVERY Universal-API-enabled marketplace instead of one
  // named marketplace — the server resolves the set from marketplace.uapi_enabled, so the client never
  // holds a list that could go stale.
  getPicker: async ({ marketplaceId, uapiEnabled, context, contextId, hotelId, accountId, partnerId }) => {
    const params = new URLSearchParams(marketplaceId ? { marketplaceId } : {});
    if (uapiEnabled) params.append('uapiEnabled', uapiEnabled);
    if (context)    params.append('context', context);
    if (contextId)  params.append('contextId', contextId);
    if (hotelId)    params.append('hotelId', hotelId);
    if (accountId)  params.append('accountId', accountId);
    if (partnerId)  params.append('partnerId', partnerId);
    const response = await api.get(`/partnermktsettings/picker?${params.toString()}`);
    return response.data;
  },

  // Mint a Cognito app client for the HCONNECT Universal API. Returns { clientId, clientSecret, scope }
  // — it does NOT save; the normal credential save stores them, so there is one write path.
  generateUapiCredentials: async ({ credentialName }) => {
    const response = await api.post('/partnermktsettings/uapi/generate-credentials', { credentialName });
    return response.data;
  },

  shareAndBind: async ({ sourceEntityType, sourceEntityId, marketplaceId, credentialId, credentialName, newSettings, shareWithHotel, shareWithAccount }) => {
    const response = await api.post('/partnermktsettings/picker/share-and-bind', {
      sourceEntityType, sourceEntityId, marketplaceId, credentialId,
      credentialName, newSettings, shareWithHotel, shareWithAccount,
    });
    return response.data;
  },
};

export default entityMktSettingsService;
